import React, { useEffect, useState, useMemo } from "react";
import { api, formatUSD } from "../lib/api";
import { useT } from "../lib/i18n";
import { Sparkle, Heart, ArrowDown } from "@phosphor-icons/react";

/**
 * Live social-proof carousel for the landing page.
 *
 * Pulls anonymized recent activity from `/api/public/recent-deliveries`
 * (cards opened by receivers, cards funded, direct-deposits approved) and
 * rotates through them every ~3.5s. We never reveal full names or exact
 * amounts — backend already enforces first-name + last-initial and rounds
 * dollars to the nearest $5 before sending the data over the wire.
 *
 * Renders nothing when:
 *  - the API errors out (we don't fake events; honesty over engagement)
 *  - there are zero events in the 48h window (fresh-deploy / quiet day)
 *
 * The 24h counters in the bottom strip ("184 cards delivered today") only
 * show once they cross a small floor so we don't display flattering single-
 * digit signal.
 */
export default function SocialProofCarousel({ theme = "light", standalone = true }) {
    const t = useT();
    const [data, setData] = useState(null);
    const [idx, setIdx] = useState(0);
    const [error, setError] = useState(false);

    useEffect(() => {
        let live = true;
        const load = () =>
            api
                .get("/public/recent-deliveries")
                .then((r) => { if (live) setData(r.data); })
                .catch(() => { if (live) setError(true); });
        load();
        // Refresh every 60s — backend caches 30s anyway, this just keeps the
        // strip fresh for users who linger on the landing page.
        const ref = setInterval(load, 60_000);
        return () => { live = false; clearInterval(ref); };
    }, []);

    const events = data?.events || [];

    useEffect(() => {
        if (events.length < 2) return;
        const ref = setInterval(() => setIdx((i) => (i + 1) % events.length), 3500);
        return () => clearInterval(ref);
    }, [events.length]);

    const stats = data?.stats || {};
    const showFundedStat = (stats.cards_funded_24h ?? 0) >= 5;
    const showDeliveredStat = (stats.cards_delivered_24h ?? 0) >= 3;

    const current = events[idx % Math.max(events.length, 1)];

    // Pre-format the visible event so the UI isn't recomputed every paint.
    const visible = useMemo(() => {
        if (!current) return null;
        const amt = formatUSD(current.amount_bucket || 0);
        const ago = relativeTimeFromIso(current.ts, t);
        return {
            ...current,
            amount: amt,
            ago,
            line: t(`social_proof.line_${current.kind}`, {
                name: current.name,
                amount: amt,
            }),
        };
    }, [current, t]);

    if (error) return null;
    if (!data) return null;
    if (events.length === 0 && !showFundedStat && !showDeliveredStat) return null;

    const dark = theme === "dark";
    const sectionCls = standalone
        ? `border-t ${dark ? "border-white/10 bg-[#0A1F16]" : "border-[#E5E0D8] bg-[#FBF9F6]"} py-8 sm:py-10`
        : "py-2";
    const eyebrowCls = dark ? "text-white/70" : "text-[#4A5C50]";
    const dotMain = dark ? "bg-[#CBA35C]" : "bg-[#184A30]";
    const lineCls = dark ? "text-[#FBF9F6]" : "text-[#112318]";
    const agoCls = dark ? "text-white/55" : "text-[#4A5C50]";
    const emptyCls = dark ? "text-white/55" : "text-[#4A5C50]";

    return (
        <section className={sectionCls} data-testid="social-proof-carousel">
            <div className={standalone ? "max-w-6xl mx-auto px-6" : ""}>
                <div className={`flex items-center gap-2 ${eyebrowCls}`}>
                    <span className="relative flex h-2 w-2">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotMain} opacity-60`} />
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${dotMain}`} />
                    </span>
                    <p className="text-[11px] tracking-[0.3em] uppercase">{t("social_proof.eyebrow")}</p>
                </div>

                <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7 min-h-[88px]">
                        {visible ? (
                            <div
                                key={`${idx}-${visible.ts}`}
                                className="fade-in flex items-start gap-3"
                                data-testid="social-proof-current-event"
                            >
                                <KindGlyph kind={visible.kind} dark={dark} />
                                <div className="flex-1">
                                    <p className={`serif text-2xl sm:text-3xl ${lineCls} leading-tight`}>
                                        {visible.line}
                                    </p>
                                    <p className={`mono text-xs ${agoCls} mt-1.5 tracking-wider`}>
                                        {visible.ago}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <p className={`text-sm ${emptyCls}`}>{t("social_proof.empty")}</p>
                        )}
                    </div>

                    <div className="lg:col-span-5 grid grid-cols-2 gap-6">
                        {showFundedStat && (
                            <Stat
                                value={stats.cards_funded_24h}
                                label={t("social_proof.stat_funded")}
                                testId="social-proof-stat-funded"
                                dark={dark}
                            />
                        )}
                        {showDeliveredStat && (
                            <Stat
                                value={stats.cards_delivered_24h}
                                label={t("social_proof.stat_delivered")}
                                testId="social-proof-stat-delivered"
                                dark={dark}
                            />
                        )}
                        {!showFundedStat && !showDeliveredStat && events.length > 0 && (
                            <Stat
                                value={events.length}
                                label={t("social_proof.stat_recent")}
                                testId="social-proof-stat-recent"
                                dark={dark}
                            />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function KindGlyph({ kind, dark }) {
    const cls = "w-9 h-9 rounded-xl flex items-center justify-center shrink-0";
    if (kind === "opened") {
        return (
            <div className={`${cls} ${dark ? "bg-[#CBA35C] text-[#0A1F16]" : "bg-[#184A30] text-[#FBF9F6]"}`}>
                <Sparkle size={18} weight="duotone" />
            </div>
        );
    }
    if (kind === "deposit") {
        return (
            <div className={`${cls} ${dark ? "bg-[#CBA35C]/20 text-[#CBA35C]" : "bg-[#CBA35C]/30 text-[#8C6B21]"}`}>
                <ArrowDown size={18} weight="duotone" />
            </div>
        );
    }
    return (
        <div className={`${cls} ${dark ? "bg-[#D96C4A]/20 text-[#F2A98C]" : "bg-[#D96C4A]/15 text-[#D96C4A]"}`}>
            <Heart size={18} weight="duotone" />
        </div>
    );
}

function Stat({ value, label, testId, dark }) {
    return (
        <div data-testid={testId}>
            <p className={`serif text-4xl sm:text-5xl ${dark ? "text-[#CBA35C]" : "text-[#184A30]"} leading-none`}>
                {Number(value).toLocaleString()}
            </p>
            <p className={`mt-2 text-[11px] tracking-[0.2em] uppercase ${dark ? "text-white/60" : "text-[#4A5C50]"}`}>{label}</p>
        </div>
    );
}

function relativeTimeFromIso(iso, t) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (!then) return "";
    const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
    if (sec < 60) return t("social_proof.ago_seconds", { n: sec });
    const min = Math.floor(sec / 60);
    if (min < 60) return t("social_proof.ago_minutes", { n: min });
    const hr = Math.floor(min / 60);
    if (hr < 48) return t("social_proof.ago_hours", { n: hr });
    return "";
}
